/**
 * Bank Logo Component
 * 
 * แสดงโลโก้ธนาคาร โดยใช้ findBankByCurrency() จาก banks.ts
 * รองรับ: id ('1','2'), code ('ktb','scb'), name ('ธนาคารกรุงไทย')
 */

import { useState } from 'react'
import { findBankByCurrency, type CurrencyCode } from '@/data/banks'
import { cn } from '@/lib/utils'
import { Building2 } from 'lucide-react'
import { useCurrency } from '@/stores/currency-store'

interface BankLogoProps {
  /** 
   * ค่าที่ใช้ค้นหาธนาคาร 
   * รองรับ: id ('1','2'), code ('ktb'), name ('ธนาคารกรุงไทย')
   */
  bankValue?: string | null
  /** 
   * สกุลเงินสำหรับค้นหา (ถ้าไม่ระบุจะใช้จาก currency store)
   */
  currency?: CurrencyCode
  /** ขนาด (default: 'md') */
  size?: 'xs' | 'sm' | 'md' | 'lg'
  /** แสดงชื่อธนาคารด้วย */
  showName?: boolean
  /** Class เพิ่มเติม */
  className?: string
}

const sizeClasses = {
  xs: 'w-5 h-5',
  sm: 'w-6 h-6',
  md: 'w-8 h-8',
  lg: 'w-10 h-10',
}

const iconSizes = {
  xs: 'w-3 h-3',
  sm: 'w-3.5 h-3.5',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
}

export function BankLogo({ 
  bankValue, 
  currency: currencyProp,
  size = 'md', 
  showName = false,
  className 
}: BankLogoProps) {
  const [imageError, setImageError] = useState(false)
  const { currency: storeCurrency } = useCurrency()
  
  // ใช้ currency ที่ส่งมา หรือจาก store
  const currencyCode = (currencyProp || storeCurrency.code) as CurrencyCode
  
  // ค้นหาธนาคารจาก banks.ts ตาม currency
  const bankInfo = bankValue ? findBankByCurrency(bankValue, currencyCode) : null
  
  // ได้รูปและข้อมูลจาก banks.ts
  const imageUrl = bankInfo?.image || '/images/banks/th/undefined.png'
  const bankName = bankInfo?.name || bankValue || '-'
  const bankColor = bankInfo?.color || '#6b7280'
  
  // ถ้าไม่มี bankValue หรือรูปโหลดไม่ได้ → แสดง fallback icon
  const showFallback = !bankValue || imageError
  
  return (
    <div className={cn("flex items-center gap-2", className)}>
      {showFallback ? (
        // Fallback: แสดง icon สีธนาคาร
        <div 
          className={cn(
            "rounded-lg flex items-center justify-center flex-shrink-0",
            sizeClasses[size]
          )}
          style={{ backgroundColor: bankValue ? bankColor : '#6b7280' }}
        >
          <Building2 className={cn("text-white", iconSizes[size])} />
        </div>
      ) : (
        // แสดงรูปโลโก้
        <img 
          src={imageUrl} 
          alt={bankName}
          className={cn("rounded-lg object-cover flex-shrink-0", sizeClasses[size])}
          onError={() => setImageError(true)}
        />
      )}
      {showName && (
        <span className="text-xs truncate max-w-[100px]" title={bankName}>
          {bankName}
        </span>
      )}
    </div>
  )
}

/**
 * Bank Account Display - แสดงโลโก้ + เลขบัญชี
 */
interface BankAccountDisplayProps {
  bankValue?: string | null
  accountNumber?: string | null
  size?: 'xs' | 'sm' | 'md'
  className?: string
}

export function BankAccountDisplay({
  bankValue,
  accountNumber,
  size = 'sm',
  className,
}: BankAccountDisplayProps) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <BankLogo bankValue={bankValue} size={size} />
      <span className="font-mono text-xs truncate max-w-[100px]" title={accountNumber || '-'}>
        {accountNumber || '-'}
      </span>
    </div>
  )
}

export default BankLogo

